import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  signOut,
  type Auth,
  type User,
} from 'firebase/auth';

import {
  configureFirebaseAuthPersistence,
  type FirebaseAuthPersistenceErrorHandlerT,
  type FirebaseAuthPersistenceT,
} from '../firebase/auth';
import { AuthContext, type AuthContextT, type AuthContextValueT, type AuthStatusT } from './AuthContext';

type AuthProviderBasePropsT = Readonly<{
  auth: Auth;
  children: ReactNode;
  persistence?: FirebaseAuthPersistenceT;
  onPersistenceError?: FirebaseAuthPersistenceErrorHandlerT;
}>;

export type FirebaseUserAuthProviderPropsT = AuthProviderBasePropsT &
  Readonly<{
    context?: AuthContextT<User>;
    mapUser?: (user: User) => User;
    onUserChanged?: (user: User | null) => void;
  }>;

export type MappedAuthProviderPropsT<TUser> = AuthProviderBasePropsT &
  Readonly<{
    context: AuthContextT<TUser>;
    mapUser: (user: User) => TUser;
    onUserChanged?: (user: TUser | null) => void;
  }>;

export type AuthProviderPropsT<TUser = User> =
  | FirebaseUserAuthProviderPropsT
  | MappedAuthProviderPropsT<TUser>;

export function AuthProvider(props: FirebaseUserAuthProviderPropsT): ReactNode;
export function AuthProvider<TUser>(props: MappedAuthProviderPropsT<TUser>): ReactNode;
export function AuthProvider<TUser = User>({
  auth,
  children,
  context,
  mapUser,
  persistence,
  onPersistenceError,
  onUserChanged,
}: AuthProviderPropsT<TUser>) {
  const [user, setUser] = useState<TUser | null>(null);
  const [status, setStatus] = useState<AuthStatusT>('loading');
  const resolvedContext = (context ?? AuthContext) as AuthContextT<TUser>;
  const Provider = resolvedContext.Provider;
  const mapUserRef = useRef(mapUser);
  const onUserChangedRef = useRef(onUserChanged);

  useEffect(() => {
    mapUserRef.current = mapUser;
    onUserChangedRef.current = onUserChanged;
  }, [mapUser, onUserChanged]);

  useEffect(() => {
    if (!persistence) return;
    configureFirebaseAuthPersistence(auth, { persistence, onPersistenceError });
  }, [auth, onPersistenceError, persistence]);

  useEffect(() => {
    return onAuthStateChanged(auth, (firebaseUser) => {
      const nextUser = firebaseUser
        ? ((mapUserRef.current?.(firebaseUser) ?? firebaseUser) as TUser)
        : null;
      setUser(nextUser);
      setStatus(firebaseUser ? 'authenticated' : 'unauthenticated');
      (onUserChangedRef.current as ((user: TUser | null) => void) | undefined)?.(nextUser);
    });
  }, [auth]);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, [auth]);

  const value = useMemo<AuthContextValueT<TUser>>(
    () => ({
      user,
      status,
      isAuthenticated: status === 'authenticated',
      isLoading: status === 'loading',
      logout,
    }),
    [logout, status, user]
  );

  return <Provider value={value}>{children}</Provider>;
}
